/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import React, { useCallback, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';

import { IconButton } from '@coursera/cds-core';
import { AddIcon, ArrowUpIcon, StopFilledIcon } from '@coursera/cds-icons';

import ChatContextAttachment from 'bundles/cds-labs/components/chat/ChatContextAttachment';
import type { ChatResponseFieldRef } from 'bundles/cds-labs/components/chat/types';

import _t from 'i18n!nls/cds-labs';

import getChatResponseFieldCss, {
  DEFAULT_MAX_ROWS,
  LINE_HEIGHT_PX,
  SINGLE_LINE_HEIGHT,
  TEXTAREA_VERTICAL_PADDING_PX,
  classes,
} from './getChatResponseFieldCss';

export type { ChatResponseFieldRef };

type AttachmentPreview = {
  id: string;
  file: File;
  /** Object URL for image files; undefined for non-image files. */
  previewUrl: string | undefined;
};

// Applied to preview <img> elements inside the media attachment slot.
const styles = {
  previewImage: css`
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  `,
  scaledIcon: css`
    display: flex;
    transform: scale(1.4);
  `,
};

function matchesAccept(file: File, accept: string): boolean {
  return accept.split(',').some((token) => {
    const t = token.trim().toLowerCase();
    if (t.startsWith('.')) return file.name.toLowerCase().endsWith(t);
    if (t.endsWith('/*')) return file.type.startsWith(t.slice(0, -1));
    return file.type === t;
  });
}

export type ChatResponseFieldProps = {
  /** Controlled value. Mutually exclusive with `defaultValue`. */
  value?: string;
  /** Initial value for uncontrolled usage. Mutually exclusive with `value`. */
  defaultValue?: string;
  placeholder?: string;
  onChange?: (value: string) => void;
  onSend?: (value: string) => void;
  /**
   * When provided, an attachment button is shown and paste-to-attach is enabled.
   * Called with the full current list of attached files whenever it changes —
   * on add (via file picker, paste, or drag-and-drop) and on remove.
   *
   * @migration Renamed from `onUpload: (files: FileList) => void`. The new
   * signature uses `File[]` instead of `FileList` and fires on removal too.
   */
  onAttachmentsChange?: (files: File[]) => void;
  /** Passed as the `accept` attribute on the hidden file input. E.g. `"image/*"` or `".pdf,.docx"`. */
  accept?: string;
  leadingAction?: React.ReactNode;
  contextAttachments?: React.ReactNode;
  trailingActions?: React.ReactNode;
  isStreaming?: boolean;
  onStop?: () => void;
  maxLength?: number;
  /** Maximum number of visible rows before the textarea starts scrolling. @default DEFAULT_MAX_ROWS */
  maxRows?: number;
  disabled?: boolean;
  autoFocus?: boolean;
  'aria-label'?: string;
  /**
   * When true, the send button is enabled and `onSend` fires even when the textarea is empty.
   * Use this when the consumer composes additional content outside the field (e.g. selected
   * suggestion chips) and wants to commit it without typed text. `onSend` receives `''` in that case.
   *
   * @default false
   */
  allowEmptySend?: boolean;
};

const ChatResponseField = React.forwardRef<ChatResponseFieldRef, ChatResponseFieldProps>(function ChatResponseField(
  props,
  ref
) {
  const {
    value,
    defaultValue,
    placeholder,
    onChange,
    onSend,
    onAttachmentsChange,
    accept,
    leadingAction,
    contextAttachments,
    trailingActions,
    isStreaming = false,
    onStop,
    maxLength,
    maxRows = DEFAULT_MAX_ROWS,
    disabled = false,
    autoFocus = false,
    'aria-label': ariaLabel = _t('Chat message input'),
    allowEmptySend = false,
  } = props;

  const maxTextareaHeight = LINE_HEIGHT_PX * maxRows + TEXTAREA_VERTICAL_PADDING_PX;

  const formRef = useRef<HTMLFormElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textWidthMeasureRef = useRef<HTMLSpanElement>(null);
  // Last known textarea clientWidth while in single-line mode; used to keep
  // multi-line stable when the textarea widens after layout switches.
  const singleLineWidthRef = useRef(0);
  const [isMultiLine, setIsMultiLine] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  // Tracks the textarea value in uncontrolled mode so canSend / isOverMaxLength
  // stay in sync with what the user typed without a consumer-managed value prop.
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue ?? '');
  const [attachmentPreviews, setAttachmentPreviews] = useState<AttachmentPreview[]>([]);
  // Debounced char-limit count for screen-reader announcements — mirrors the CDS
  // CharacterLimit component pattern. The visual span always shows the live count
  // (aria-hidden) while this state drives a live region that only updates after the
  // user pauses typing, so the screen reader can complete each announcement without
  // being cut off mid-word by rapid keystrokes.
  const [announcedRemaining, setAnnouncedRemaining] = useState<number | undefined>(undefined);
  const charLimitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingCharLimitRef = useRef(false);
  const prevRemainingRef = useRef<number | undefined>(undefined);
  const latestRemainingRef = useRef<number | undefined>(undefined);
  const isCharLimitMountRef = useRef(true);
  // Ref-tracked so the cleanup effect (empty deps) can revoke all remaining URLs,
  // and so attachment handlers can read the current list without stale closures.
  const createdUrlsRef = useRef<string[]>([]);
  const attachmentPreviewsRef = useRef<AttachmentPreview[]>([]);
  const attachmentIdCounterRef = useRef(0);
  attachmentPreviewsRef.current = attachmentPreviews;

  // Derived values — computed early so they are available in effects below.
  const currentValue = value ?? uncontrolledValue;
  const isOverMaxLength = maxLength !== undefined && currentValue.length > maxLength;
  const canSend = !isStreaming && !disabled && !isOverMaxLength && (allowEmptySend || currentValue.trim().length > 0);
  const remaining = maxLength !== undefined ? maxLength - currentValue.length : undefined;
  // Always keep the ref current so the debounced SR callback announces the latest value.
  latestRemainingRef.current = remaining;

  React.useImperativeHandle(ref, () => ({
    focus: () => textareaRef.current?.focus(),
    clear: () => {
      if (value !== undefined) {
        // In controlled mode the value prop overrides any DOM mutation on re-render.
        // Callers must reset their own state instead of calling clear().
        return;
      }
      if (textareaRef.current) {
        textareaRef.current.value = '';
        updateHeight(textareaRef.current);
      }
      setUncontrolledValue('');
      if (attachmentPreviewsRef.current.length > 0) {
        setAttachmentPreviews([]);
        onAttachmentsChange?.([]);
      }
    },
  }));

  // Revoke all object URLs when the component unmounts to avoid memory leaks.
  useEffect(
    () => () => {
      createdUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    },
    []
  );

  const updateHeight = useCallback(
    (el: HTMLTextAreaElement) => {
      el.style.height = 'auto';
      const newHeight = Math.min(el.scrollHeight, maxTextareaHeight);
      el.style.height = `${newHeight}px`;

      // Sync the hidden span so we can measure the text's natural single-line width.
      if (textWidthMeasureRef.current) {
        textWidthMeasureRef.current.textContent = el.value;
      }

      // Guard: element not yet laid out — leave state unchanged.
      if (el.clientWidth !== 0) {
        const textWidth = textWidthMeasureRef.current?.scrollWidth ?? 0;

        // Compute the next multi-line state and perform the ref mutation outside the
        // setter so the updater function remains pure (React Strict Mode double-invokes
        // updaters to surface side-effects, so mutations inside setters are unsafe).
        let nextIsMultiLine: boolean;
        if (!isMultiLine) {
          // Currently single-line: save the narrow textarea width so that when the
          // component later switches to multi-line (and el.clientWidth becomes the full
          // width), we can still compare against the original narrow threshold.
          singleLineWidthRef.current = el.clientWidth;
          // Switch when text fills the line (before it wraps) or when an explicit
          // newline already made content taller than one line.
          nextIsMultiLine = textWidth >= el.clientWidth || el.scrollHeight > SINGLE_LINE_HEIGHT;
        } else {
          // Currently multi-line: compare against the saved *narrow* width, not
          // el.clientWidth (which is the full textarea width here). Updating the ref
          // to el.clientWidth would raise the threshold and cause text that only fills
          // the narrow slot to snap back to single-line on every keystroke.
          nextIsMultiLine = textWidth >= singleLineWidthRef.current || el.scrollHeight > SINGLE_LINE_HEIGHT;
        }

        if (nextIsMultiLine !== isMultiLine) {
          setIsMultiLine(nextIsMultiLine);
        }
      }
    },
    [isMultiLine, maxTextareaHeight]
  );

  useEffect(() => {
    if (textareaRef.current) {
      updateHeight(textareaRef.current);
    }
  }, [value, updateHeight]);

  // Char-limit screen-reader debounce — mirrors the CDS CharacterLimit component.
  // Updates the live region only after the user pauses typing (1 s) so the screen
  // reader can complete each announcement uninterrupted. Announces only when crossing
  // key thresholds to avoid overwhelming users with continuous updates.
  useEffect(() => {
    // Skip the initial mount — nothing to announce before the user types.
    if (isCharLimitMountRef.current) {
      isCharLimitMountRef.current = false;
      return;
    }
    if (maxLength === undefined || remaining === undefined) return;

    const prev = prevRemainingRef.current;
    prevRemainingRef.current = remaining;

    if (prev !== undefined) {
      const ratio = remaining / maxLength;
      const prevRatio = prev / maxLength;

      // Fire on threshold crossings so fast typing that skips a threshold still
      // triggers an announcement: entering/leaving the ≤20% zone, or restoring to full.
      // `remaining <= 0` fires on every keystroke while over the limit — unlike TextField,
      // ChatResponseField allows typing past maxLength, so the condition must stay true
      // continuously (not just on the first crossing) so the debounce timer keeps firing
      // after each pause and the screen reader hears the current over-limit state.
      const crossedThreshold = ratio === 1 || prevRatio > 0.2 !== ratio > 0.2 || remaining <= 0;

      if (crossedThreshold) {
        pendingCharLimitRef.current = true;
      }
    }

    if (!pendingCharLimitRef.current) return;

    if (charLimitTimerRef.current) clearTimeout(charLimitTimerRef.current);
    charLimitTimerRef.current = setTimeout(() => {
      setAnnouncedRemaining(latestRemainingRef.current);
      pendingCharLimitRef.current = false;
      charLimitTimerRef.current = null;
    }, 1000);
  }, [maxLength, remaining]);

  // Clean up any pending char-limit timer on unmount.
  useEffect(() => {
    return () => {
      if (charLimitTimerRef.current) clearTimeout(charLimitTimerRef.current);
    };
  }, []);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      updateHeight(e.currentTarget);
      if (value === undefined) {
        setUncontrolledValue(e.currentTarget.value);
      }
      onChange?.(e.currentTarget.value);
    },
    [onChange, updateHeight, value]
  );

  // Consolidates all send logic. The form submit event is used so that IME
  // composition (e.g. Japanese kanji selection) never accidentally triggers a
  // send — browsers do not fire submit during an active composition session.
  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!canSend) return;
      const raw = textareaRef.current?.value ?? '';
      onSend?.(raw.trim());
    },
    [canSend, onSend]
  );

  // Pressing Enter (without Shift, and not during IME composition) triggers a
  // form submission via requestSubmit() rather than calling onSend directly.
  // This ensures the isComposing guard is always respected: requestSubmit()
  // fires a proper submit event which browsers skip during composition, and
  // handleSubmit owns all the canSend validation in one place.
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      formRef.current?.requestSubmit();
    }
  }, []);

  // Shared logic for adding files from any source (file picker or paste).
  // Reads current previews from the ref so it is always fresh without being
  // listed as a stale-closure dependency.
  const addFiles = useCallback(
    (files: File[]) => {
      const newPreviews = files.map((file) => {
        const isImage = file.type.startsWith('image/');
        const previewUrl = isImage ? URL.createObjectURL(file) : undefined;
        if (previewUrl) createdUrlsRef.current.push(previewUrl);
        const id = String(attachmentIdCounterRef.current);
        attachmentIdCounterRef.current += 1;
        return { id, file, previewUrl };
      });
      const next = [...attachmentPreviewsRef.current, ...newPreviews];
      setAttachmentPreviews(next);
      onAttachmentsChange?.(next.map((p) => p.file));
    },
    [onAttachmentsChange]
  );

  const handleUploadClick = useCallback(() => fileInputRef.current?.click(), []);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = e.currentTarget;
      if (!files || files.length === 0) return;
      addFiles(Array.from(files));
      // Reset so the same file can be selected again.
      e.currentTarget.value = '';
    },
    [addFiles]
  );

  // Intercepts paste events that contain files (e.g. a copied image). Plain text
  // paste is unaffected — clipboardData.files is empty in that case.
  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      if (!onAttachmentsChange) return;
      const pastedFiles = Array.from(e.clipboardData.files)
        .filter((f) => f.size > 0)
        .filter((f) => !accept || matchesAccept(f, accept));
      if (pastedFiles.length === 0) return;
      e.preventDefault();
      addFiles(pastedFiles);
    },
    [onAttachmentsChange, accept, addFiles]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLFormElement>) => {
      if (!onAttachmentsChange) return;
      e.preventDefault();
      setIsDraggingOver(true);
    },
    [onAttachmentsChange]
  );

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLFormElement>) => {
    // Only clear when the pointer truly leaves the form, not when it moves between children.
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDraggingOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLFormElement>) => {
      if (!onAttachmentsChange) return;
      e.preventDefault();
      setIsDraggingOver(false);
      const droppedFiles = Array.from(e.dataTransfer.files)
        .filter((f) => f.size > 0)
        .filter((f) => !accept || matchesAccept(f, accept));
      if (droppedFiles.length === 0) return;
      addFiles(droppedFiles);
    },
    [onAttachmentsChange, accept, addFiles]
  );

  const handleRemovePreview = useCallback(
    (id: string) => {
      const item = attachmentPreviewsRef.current.find((p) => p.id === id);
      if (item?.previewUrl) {
        URL.revokeObjectURL(item.previewUrl);
        createdUrlsRef.current = createdUrlsRef.current.filter((u) => u !== item.previewUrl);
      }
      const next = attachmentPreviewsRef.current.filter((p) => p.id !== id);
      setAttachmentPreviews(next);
      onAttachmentsChange?.(next.map((p) => p.file));
    },
    [onAttachmentsChange]
  );

  const stopLabel = _t('Stop');
  const sendLabel = _t('Send message');

  const uploadButton = onAttachmentsChange ? (
    <>
      <input
        ref={fileInputRef}
        accept={accept}
        className={classes.hiddenFileInput}
        multiple
        type="file"
        onChange={handleFileInputChange}
      />
      <IconButton
        aria-label={_t('Attach file')}
        icon={
          <span css={styles.scaledIcon}>
            <AddIcon />
          </span>
        }
        size="small"
        tooltip={_t('Attach file')}
        type="button"
        variant="ghostSecondary"
        onClick={handleUploadClick}
      />
    </>
  ) : null;

  const sendButton = isStreaming ? (
    <IconButton
      aria-label={stopLabel}
      className={classes.sendButton}
      disabled={disabled}
      icon={<StopFilledIcon />}
      size="small"
      tooltip={stopLabel}
      type="button"
      variant="primary"
      onClick={onStop}
    />
  ) : (
    <IconButton
      aria-label={sendLabel}
      className={classes.sendButton}
      disabled={!canSend}
      icon={<ArrowUpIcon />}
      size="small"
      tooltip={sendLabel}
      type="submit"
      variant="primary"
    />
  );

  // Show the visual char-limit counter when the user is in the last 20% of the
  // limit or has gone over, satisfying WCAG 2.2 SC 3.3.1 / 4.1.3 (status messages).
  const shouldAnnounceCharLimit =
    maxLength !== undefined && remaining !== undefined && (remaining <= Math.ceil(maxLength * 0.2) || isOverMaxLength);

  // Debounced values for the SR live region.
  const isAnnouncedOverMaxLength =
    maxLength !== undefined && announcedRemaining !== undefined && announcedRemaining < 0;
  const shouldAnnounceToSr =
    announcedRemaining !== undefined &&
    maxLength !== undefined &&
    (announcedRemaining <= Math.ceil(maxLength * 0.2) || isAnnouncedOverMaxLength);

  // Two separate elements mirror the CDS CharacterLimit pattern:
  //
  // 1. Visual span (aria-hidden): always reflects the live count so sighted users
  //    see an immediate update on every keystroke. Styled with charLimit/charLimitHidden
  //    so it only occupies flex space when visible.
  //
  // 2. SR live region (role="status"): shows announcedRemaining, which is updated only
  //    after the user pauses typing (1 s debounce) and only at threshold crossings.
  //    This prevents the screen reader from being interrupted mid-word on every keystroke,
  //    which was causing garbled announcements (e.g. reading adjacent page content).
  //    role="status" implies aria-live="polite" and aria-atomic="true" per the ARIA spec.
  //    Always in the DOM so the live region is registered on mount.
  //
  // No aria-describedby on the textarea: that would double-announce on focus + on update.
  const charLimitText = (
    <>
      {/* Visual display — hidden from screen readers, always shows the live count */}
      <span
        aria-hidden
        className={
          shouldAnnounceCharLimit
            ? classNames(classes.charLimit, isOverMaxLength && classes.charLimitError)
            : classes.charLimitHidden
        }
      >
        {shouldAnnounceCharLimit &&
          (isOverMaxLength
            ? // Non-shorthand syntax is intentional: shorthand `{ maxLength }` lets terser
              // mangle both the variable AND the derived object key to the same short name
              // (e.g. `{ h: h }`), breaking _t's lookup. Explicit form mangles only the value.
              _t('Maximum #{maxLength} characters', { maxLength: maxLength })
            : _t('#{remaining} characters remaining', { remaining: remaining }))}
      </span>
      {/* Screen-reader live region — debounced, threshold-based */}
      <span className={classes.charLimitAnnouncement} role="status">
        {shouldAnnounceToSr &&
          announcedRemaining !== undefined &&
          maxLength !== undefined &&
          (isAnnouncedOverMaxLength
            ? _t('Maximum #{maxLength} characters', { maxLength: maxLength })
            : _t('#{remaining} characters remaining', { remaining: announcedRemaining }))}
      </span>
    </>
  );

  const actionRowContent = (
    <>
      {charLimitText}
      {trailingActions && <div className={classes.trailingActionsWrapper}>{trailingActions}</div>}
      {sendButton}
    </>
  );

  // Inline previews for locally selected files. Rendered before consumer-provided
  // contextAttachments so newly added items appear on the left.
  const allImages = attachmentPreviews.length > 0 && attachmentPreviews.every((p) => p.previewUrl !== undefined);
  const mediaSize = allImages && attachmentPreviews.length <= 4 ? 'default' : 'small';

  const internalPreviews = attachmentPreviews.map((preview) =>
    preview.previewUrl ? (
      <ChatContextAttachment
        key={preview.id}
        type="media"
        size={mediaSize}
        onRemove={() => handleRemovePreview(preview.id)}
      >
        <img alt={preview.file.name} css={styles.previewImage} src={preview.previewUrl} />
      </ChatContextAttachment>
    ) : (
      <ChatContextAttachment
        key={preview.id}
        type="metadata"
        label={preview.file.name}
        onRemove={() => handleRemovePreview(preview.id)}
      />
    )
  );

  const hasContextRow = internalPreviews.length > 0 || Boolean(contextAttachments);

  // The upload button lives in the leading-action slot so it always appears in
  // the bottom-left corner regardless of single-line vs. multi-line layout.
  const leadingContent =
    uploadButton || leadingAction ? (
      <div className={classes.leadingAction}>
        {uploadButton}
        {leadingAction}
      </div>
    ) : null;

  return (
    <form
      ref={formRef}
      className={classNames(
        classes.root,
        isMultiLine && classes.multiLine,
        isStreaming && classes.streaming,
        isDraggingOver && classes.dragging
      )}
      css={getChatResponseFieldCss}
      data-testid="chat-response-field"
      noValidate
      style={{ '--crf-max-textarea-height': `${maxTextareaHeight}px` } as React.CSSProperties}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onSubmit={handleSubmit}
    >
      <div aria-hidden="true" className={classes.focusRing} />
      {/* Measures text natural width to detect line-fill before wrapping occurs. */}
      <span ref={textWidthMeasureRef} aria-hidden="true" className={classes.textMeasure} />
      {/* Announces drag-over state to screen reader users. Always in the DOM so the live region
          is registered on mount. role="status" implies aria-live="polite" + aria-atomic="true". */}
      <span className={classes.dragAnnouncement} role="status">
        {onAttachmentsChange && isDraggingOver ? _t('Drop to attach files') : ''}
      </span>

      {hasContextRow && (
        <div className={classes.contextRow}>
          {internalPreviews}
          {contextAttachments}
        </div>
      )}

      <div className={classes.textRow}>
        {!isMultiLine && leadingContent}
        <textarea
          ref={textareaRef}
          aria-label={ariaLabel}
          autoFocus={autoFocus}
          className={classes.textarea}
          rows={1}
          {...(value !== undefined ? { value } : { defaultValue })}
          disabled={disabled}
          placeholder={placeholder}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          data-testid="chat-response-field-textarea"
        />
        {!isMultiLine && <div className={classes.actionRow}>{actionRowContent}</div>}
      </div>

      {isMultiLine && (
        <div className={classes.actionRow}>
          {leadingContent}
          <div className={classes.actionRowRight}>{actionRowContent}</div>
        </div>
      )}
    </form>
  );
});

export default ChatResponseField;
